import axios from "axios";
import { getCredentials } from "@/helpers/credentials";
import { ADMIN_CREDENTIALS_STORAGE_KEY } from "@/constants/auth";
import * as types from "./types";

let kpiClient = undefined;
const getClientInstance = () => {
  if (!kpiClient) {
    kpiClient = axios.create({
      baseURL: process.env.VUE_APP_KPI_ENDPOINT,
      withCredentials: false,
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "Content-Type": "application/json",
      },
    });

    // Add interceptor to dynamically add token on each request
    kpiClient.interceptors.request.use(
      config => {
        const credentials = getCredentials(ADMIN_CREDENTIALS_STORAGE_KEY);
        if (credentials?.token) {
          config.headers.Authorization = `Bearer ${credentials.token}`;
        }
        return config;
      },
      error => Promise.reject(error)
    );
  }
  return kpiClient;
};

export default {
  [types.SEARCH_CONSULTANTS]: ({ commit }, payload) => {
    const resource = `/api/consultants/search?page=${payload.currentPage}`;
    delete payload.currentPage;
    return axios
      .post(resource, payload)
      .then((resp) => {
        commit(types.MUTATE_CONSULTANTS_SEARCH_RESULT, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },

  [types.GET_DNA_HISTORY]: (_, payload) => {
    const { id, ...data } = payload;
    return getClientInstance()
      .get(`/api/consultant_dna/history/${payload.id}`, { params: data })
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.SEARCH_IMPORTS]: ({ commit }, payload) => {
    const resource = `/api/consultant_ead_import/imports?page=${payload.currentPage}`;
    delete payload.currentPage;
    return getClientInstance()
      .post(resource, payload)
      .then((resp) => {
        commit(types.MUTATE_IMPORTS_SEARCH_RESULT, resp.data.data);
        return Promise.resolve(resp.data);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.SEARCH_IMPORT_HISTORY]: ({ commit }, payload) => {
    const resource = `/api/consultant_ead_import/history_imports/${payload.idImport}?page=${payload.currentPage}`;
    delete payload.currentPage;
    return getClientInstance()
      .get(resource, payload)
      .then((resp) => {
        commit(types.MUTATE_IMPORT_HISTORY_SEARCH_RESULT, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_CONSULTANT]: ({}, payload) => {
    return axios
      .get(`/api/consultants/${payload}`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_CEP]: ({}, payload) => {
    return axios
      .get(`/api/addresses/by_cep/${payload}`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_CONSULTANT_INFO]: (_) => {
    return axios
      .get(`/api/consultants/info`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.SAVE_EMPLOYEE]: ({}, payload) => {
    let save = axios.post;
    let route = "/api/consultants/employee";
    if (payload.id) {
      save = axios.put;
    }
    return save(route, payload)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.SAVE_CONSULTANT]: ({}, payload) => {
    return axios
      .post("/api/consultants", payload)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.SAVE_CONSULTANT_BASIC_DATA]: ({}, payload) => {
    return axios
      .put(`/api/consultants/${payload.id}/basic_information`, payload)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.SAVE_CONSULTANT_COMPANY_DATA]: ({}, payload) => {
    return axios
      .post("/api/consultants/company_information", payload)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.DELETE_CONSULTANT]: ({}, payload) => {
    return axios
      .delete(`/api/consultants/${payload.id || payload}`, {
        data: payload.id
          ? {
              termination_reason_id: payload.termination_reason_id,
              additional_details: payload.additional_details,
            }
          : undefined,
      })
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_CONSULTANT_TERMINATION_REASONS]: ({ commit }) => {
    return axios
      .get("/api/consultants/termination_reasons")
      .then((resp) => {
        commit(types.MUTATE_CONSULTANT_TERMINATION_REASONS, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.DELETE_IMPORT]: ({}, payload) => {
    return getClientInstance()
      .delete(`/api/consultant_ead_import/import/${payload}`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_DETAILS_GENERAL_INFORMATION]: ({ commit }, payload) => {
    commit(types.CLEAR_DETAILS_GENERAL_INFORMATION);
    return axios
      .get(`/api/consultants/${payload}/details/general_information`)
      .then((resp) => {
        commit(types.MUTATE_DETAILS_GENERAL_INFORMATION, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_SUB_ROLE_PROGRESS]: (_, payload) => {
    return axios.get(`/api/consultants/${payload}/career/sub_role_progress`);
  },
  [types.GET_SUB_ROLE_CATALOG]: () => {
    return axios.get("/api/consultants/career/sub_roles");
  },
  [types.GET_DETAILS_COMPANY_DATA]: ({ commit }, payload) => {
    return axios
      .get(`/api/consultants/${payload}/details/company`)
      .then((resp) => {
        commit(types.MUTATE_DETAILS_COMPANY_DATA, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_DETAILS_NETWORK_DATA]: ({ commit }, payload) => {
    return axios
      .get(`/api/consultants/${payload}/details/network`)
      .then((resp) => {
        commit(types.MUTATE_DETAILS_NETWORK_DATA, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.UPDATE_CONSULTANT_CONTRACT_SIGNATURE_DATE]: ({ commit }, payload) => {
    return axios
      .patch(`/api/consultants/${payload}/document/contract_date`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.UPDATE_CONSULTANT_CONTRACT_TERMINATION_SIGNATURE_DATE]: (
    _,
    payload
  ) => {
    return axios
      .patch(`/api/consultants/${payload}/document/contract_termination_date`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.OPEN_NEW_CONSULTANT_SIDEBAR]: ({ commit }, payload) => {
    commit(types.MUTATE_NEW_CONSULTANT_SIDEBAR, { ...payload, visible: true });
  },
  [types.OPEN_NEW_EMPLOYEE_SIDEBAR]: ({ commit }, payload) => {
    commit(types.MUTATE_NEW_EMPLOYEE_SIDEBAR, { ...payload, visible: true });
  },
  [types.GET_TRANSFERRED_CONSULTANTS]: ({ commit }, payload) => {
    const resource = `/api/consultants/transferred?page=${payload.currentPage}`;
    delete payload.currentPage;
    return axios
      .post(resource, payload)
      .then((resp) => {
        commit(types.MUTATE_TRANSFERRED_CONSULTANTS, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.TRANSFER_CONSULTANTS]: ({}, payload) => {
    return axios
      .post(`/api/consultants/transfer_consultants`, payload)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_TRANSFERABLE_CONSULTANTS]: ({ commit }, payload) => {
    return axios
      .get(`/api/consultants/transferable`)
      .then((resp) => {
        commit(types.MUTATE_TRANSFERABLE_CONSULTANTS, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_DIRECT_TEAM]: ({ commit }, payload) => {
    return axios
      .get(`/api/consultants/${payload}/direct_team`)
      .then((resp) => {
        commit(types.MUTATE_DIRECT_TEAM, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.OPEN_CHANGE_CONSULTANT_ROLE_SIDEBAR]: ({ commit }, payload) => {
    commit(types.MUTATE_CHANGE_CONSULTANT_ROLE_SIDEBAR, {
      ...payload,
      visible: true,
    });
  },
  [types.OPEN_CHANGE_PHYSICAL_FRANCHISE_SIDEBAR]: ({ commit }, payload) => {
    commit(types.MUTATE_CHANGE_PHYSICAL_FRANCHISE_SIDEBAR, {
      ...payload,
      visible: true,
    });
  },
  [types.CHANGE_CONSULTANT_ROLE]: ({}, payload) => {
    return axios
      .post(`/api/consultants/change_role`, payload)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.CHANGE_PHYSICAL_FRANCHISE]: ({}, payload) => {
    return axios
      .post(`/api/consultants/change_physical_franchise`, payload)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_ROLE_CHANGE_IS_VALID]: ({}, payload) => {
    return axios
      .post(`/api/consultants/validate_pnce_consultants`, payload)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_AFFILIATION_TYPES]: ({ commit }, payload) => {
    return axios
      .get(`/api/consultants/affiliation_types`)
      .then((resp) => {
        commit(types.MUTATE_AFFILIATION_TYPES, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.DOWNLOAD_ON_CONSULTANTS]: ({ commit }, payload) => {
    return axios
      .get(`/api/consultants/download/${payload.id}`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.SAVE_IMPORT_EAD]: ({ commit }, payload) => {
    return getClientInstance()
      .post(`/api/consultant_ead_import/import`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.SAVE_DNA_CAMPAIGN]: ({}, payload) => {
    return getClientInstance()
      .post(`/api/consultant_dna/history`, payload)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_DNA_CAMPAIGN]: ({}, payload) => {
    return getClientInstance()
      .get(`/api/consultant_dna/campaign/${payload.consultant_id}`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.OPEN_IMPORT_EAD_SIDEBAR]: ({ commit }, payload) => {
    commit(types.MUTATE_IMPORT_EAD_SIDEBAR, { ...payload, visible: true });
  },
  [types.GET_HAS_PROMOTION_CHECKLIST]: ({}, payload) => {
    return axios
      .get(`/api/consultants/has_promotion_checklist/${payload}`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_HAS_PENDING_PROMOTION]: ({}, payload) => {
    return axios
      .get(`/api/consultants/has_pending_promotion/${payload}`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_PENDING_PROMOTION_STEPS]: ({ commit }, payload) => {
    return axios
      .get(`/api/consultants/${payload}/promotion`)
      .then((resp) => {
        commit(types.MUTATE_PENDING_PROMOTION_STEPS, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.UPDATE_PROMOTION_STEP]: ({}, payload) => {
    const resource = `/api/consultants/${payload.consultant_id}/promotion_step`;
    delete payload.consultant_id;
    return axios
      .put(resource, payload)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.SAVE_NETWORKER]: ({ commit }, payload) => {
    let save = axios.post;
    let route = "/api/consultants/networker";
    if (payload.id) {
      save = axios.put;
    }
    return save(route, payload)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_NETWORKER]: ({ commit }, payload) => {
    return axios
      .get(`/api/consultants/networker/${payload}`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_EMPLOYEE]: ({ commit }, payload) => {
    return axios
      .get(`/api/consultants/employee/${payload}`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_DETAILS_INDICATORS_SUMMARY]: ({ commit }, payload) => {
    return axios
      .get(`/api/consultants/indicators/${payload}/summary`)
      .then((resp) => {
        commit(types.MUTATE_DETAILS_INDICATORS_SUMMARY, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },

  [types.GET_DETAILS_APPOINTMENT_CLOSURE_RATES]: ({ commit }, payload) => {
    return axios
      .get(`/api/consultants/indicators/appointment_closure_rates/${payload}`)
      .then((resp) => {
        commit(types.MUTATE_DETAILS_APPOINTMENT_CLOSURE_RATES, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },

  [types.GET_DETAILS_QUALITATIVE_HIGHLIGHTS]: ({ commit }, payload) => {
    return axios
      .get(
        `/api/consultants/indicators/${payload}/specialist_qualitative_highlight_statistics`
      )
      .then((resp) => {
        commit(types.MUTATE_DETAILS_QUALITATIVE_HIGHLIGHTS, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  
  [types.GET_CONSULTANT_CERTIFICATE_TYPES]: ({ commit }) => {
    return axios
      .get("/api/consultant_certificates/certificate_types")
      .then((resp) => {
        commit(types.MUTATE_CONSULTANT_CERTIFICATE_TYPES, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },

  [types.SAVE_CONSULTANT_CERTIFICATE]: ({}, payload) => {
    return axios
      .post(`/api/consultant_certificates`, payload)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_PROMOTION_CONTRACT_RULES]: ({ commit }) => {
    return axios
      .get('/api/consultants/promotion_contract_rules')
      .then((resp) => {
        commit(types.MUTATE_PROMOTION_CONTRACT_RULES, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },

  [types.SEARCH_ROLE_PROMOTIONS]: ({ commit }, payload) => {
    const resource = `/api/consultant_role_promotions/search?page=${payload.currentPage}`;
    delete payload.currentPage;
    return axios
      .post(resource, payload)
      .then((resp) => {
        commit(types.MUTATE_ROLE_PROMOTION_SEARCH_RESULT, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },

  [types.GET_ROLE_PROMOTION]: ({}, payload) => {
    return axios
      .get(`/api/consultant_role_promotions/${payload}`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },

  [types.GET_CONSULTANT_CERTIFICATE]: ({ }, payload) => {
    return axios
      .get(`/api/consultant_certificates/${payload}`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_CONSULTANT_CERTIFICATE_REQUESTS]: ({ }, consultantId) => {
    return axios
      .get(`/api/consultant_certificates/consultant/${consultantId}`)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.LEADER_REVIEW_CONSULTANT_CERTIFICATE]: ({ }, payload) => {
    return axios
      .post(`/api/consultant_certificates/${payload.id}/leader_review`, {
        decision: payload.decision,
        reason: payload.reason,
      })
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.UPDATE_ROLE_PROMOTION]: ({}, payload) => {
    return axios
      .put(`/api/consultant_role_promotions/${payload.id}`, payload)
      .then((resp) => {
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },

  [types.UPDATE_CONSULTANT_CERTIFICATE]: ({}, payload) => {
    return axios
      .post(`/api/consultant_certificates/${payload.id}`, payload)
      .then((resp) => { 
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  [types.GET_ROLE_PROMOTION_STATUS_TYPES]: ({ commit }) => {
    return axios
      .get(`/api/consultant_role_promotions/status_types`)
      .then((resp) => {
        commit(types.MUTATE_ROLE_PROMOTION_STATUS_TYPES, resp.data);
        return Promise.resolve(resp);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  },
  
  [types.DOWNLOAD_CERTIFICATE]: (_, id) => {
    return axios.get(`/api/consultant_certificates/download/${id}`, {
      responseType: "blob",
    });
  },

  [types.OPEN_ROLE_PROMOTION_SIDEBAR]: ({ commit }, payload) => {
    commit(types.MUTATE_ROLE_PROMOTION_SIDEBAR, { ...payload, visible: true });
  },
  [types.GET_CONSULTANT_ROLES_HISTORY]: ({}, payload) => {
    return axios.get(`/api/consultants/role_history/${payload}`)
      .then(resp => {
        return Promise.resolve(resp)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  [types.GET_PHYSICAL_FRANCHISE_CHANGE_HISTORY]: ({}, payload) => {
    return axios.get(`/api/consultants/physical_franchise_history/${payload}`)
      .then(resp => {
        return Promise.resolve(resp)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  [types.EXTEND_CONSULTANT_DEADLINE]: ({}, payload) => {
    return axios.post(`/api/consultants/extend_personal_data_deadline`, payload)
      .then(resp => {
        return Promise.resolve(resp)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  [types.PROCESS_CONSULTANT_ROLE_PROMOTION]: ({}, payload) => {
    return axios.get(`/api/consultant_role_promotions/process/${payload}`)
      .then(resp => {
        return Promise.resolve(resp)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  [types.GET_PNS_PENDING_ONBOARDING]: ({ commit }, scope = 'structure') => {
    return axios.get('/api/consultants/pns_pending_onboarding', { params: { scope } })
      .then(resp => {
        commit(types.MUTATE_PNS_PENDING_ONBOARDING, resp.data.data)
        return Promise.resolve(resp)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
};
